"use client";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  ShieldCheck,
  Lock,
  ScrollText,
  ChevronRight,
  Menu,
  X,
  Calendar,
  List,
  Check,
  Mail,
  type LucideIcon,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────────
// Public legal-document renderer used by the AML, Privacy, and Terms of Use
// pages. Designed for long-form readability: sticky table of contents on
// desktop, comfortable line-length, generous spacing, and deep-linkable
// section anchors so support/legal can share specific clauses by URL.
// ──────────────────────────────────────────────────────────────────────────────

export type PolicyBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "h3"; text: string };

export type PolicySection = {
  id: string;        // url anchor (no leading hash)
  number: string;    // "1", "5.1", "7.2", …
  title: string;
  level?: 1 | 2;     // 1 = top-level section (default), 2 = subsection
  blocks: PolicyBlock[];
};

export type PolicyData = {
  slug: "aml-policy" | "privacy-policy" | "terms-of-use";
  title: string;
  subtitle?: string;
  effectiveDate: string;
  intro?: string;
  icon: LucideIcon;          // hero icon
  documentType: string;      // small badge label (e.g. "Compliance Policy")
  version: string;           // shown in the hero meta row
  sections: PolicySection[];
  footerNote?: string;
};

// Cross-links shown at the bottom of each page. Re-use the same lucide
// icons each policy ships, so the relationship is visually consistent.
const RELATED: Record<
  PolicyData["slug"],
  Array<{ slug: string; title: string; href: string; icon: LucideIcon; subtitle: string }>
> = {
  "aml-policy": [
    {
      slug: "privacy-policy",
      title: "Privacy & Data Policy",
      href: "/privacy-policy",
      icon: Lock,
      subtitle: "How we collect, use, and protect your data",
    },
    {
      slug: "terms-of-use",
      title: "Terms of Use",
      href: "/terms-of-use",
      icon: ScrollText,
      subtitle: "Customer & business terms of service",
    },
  ],
  "privacy-policy": [
    {
      slug: "aml-policy",
      title: "AML & BSA Compliance",
      href: "/aml-policy",
      icon: ShieldCheck,
      subtitle: "Our anti-money-laundering framework",
    },
    {
      slug: "terms-of-use",
      title: "Terms of Use",
      href: "/terms-of-use",
      icon: ScrollText,
      subtitle: "Customer & business terms of service",
    },
  ],
  "terms-of-use": [
    {
      slug: "aml-policy",
      title: "AML & BSA Compliance",
      href: "/aml-policy",
      icon: ShieldCheck,
      subtitle: "Our anti-money-laundering framework",
    },
    {
      slug: "privacy-policy",
      title: "Privacy & Data Policy",
      href: "/privacy-policy",
      icon: Lock,
      subtitle: "How we collect, use, and protect your data",
    },
  ],
};

function Block({ block }: { block: PolicyBlock }) {
  if (block.type === "p") {
    return (
      <p className="text-gray-300 leading-relaxed text-[15px]">{block.text}</p>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 className="text-white font-semibold text-base mt-3 flex items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block w-1 h-4 rounded-full bg-gradient-to-b from-[#61CE70] to-[#50B060]"
        />
        {block.text}
      </h3>
    );
  }
  return (
    <ul className="space-y-2">
      {block.items.map((item, i) => (
        <li
          key={i}
          className="flex gap-3 text-gray-300 text-[15px] leading-relaxed"
        >
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#61CE70]/10 border border-[#61CE70]/20"
          >
            <Check className="h-3 w-3 text-[#61CE70]" strokeWidth={3} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ section }: { section: PolicySection }) {
  const isSub = section.level === 2;
  return (
    <section
      id={section.id}
      className={`scroll-mt-24 ${isSub ? "mt-8" : "mt-14"} first:mt-0`}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          aria-hidden="true"
          className={`inline-flex shrink-0 items-center justify-center rounded-lg font-semibold tabular-nums ${
            isSub
              ? "min-w-[2.25rem] h-7 px-2 text-xs bg-[#61CE70]/10 text-[#61CE70]/80 border border-[#61CE70]/15"
              : "min-w-[2.5rem] h-8 px-2 text-sm bg-gradient-to-br from-[#61CE70]/20 to-[#50B060]/10 text-[#61CE70] border border-[#61CE70]/25 shadow-sm shadow-[#61CE70]/10"
          }`}
        >
          {section.number}
        </span>
        <h2
          className={`text-white font-bold tracking-tight leading-tight ${
            isSub ? "text-lg pt-0.5" : "text-xl sm:text-2xl pt-0.5"
          }`}
        >
          {section.title}
        </h2>
      </div>
      <div className="space-y-4 pl-0 sm:pl-[3.25rem]">
        {section.blocks.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>
    </section>
  );
}

function TableOfContents({ sections }: { sections: PolicySection[] }) {
  return (
    <nav aria-label="Table of contents" className="space-y-0.5">
      {sections.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={`group flex items-start gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors hover:bg-white/5 hover:text-white ${
            s.level === 2
              ? "ml-5 text-gray-500 hover:text-gray-300"
              : "text-gray-400"
          }`}
        >
          <span className="tabular-nums text-[#61CE70]/70 group-hover:text-[#61CE70] shrink-0 min-w-[1.75rem]">
            {s.number}
          </span>
          <span className="min-w-0">{s.title}</span>
        </a>
      ))}
    </nav>
  );
}

export default function PolicyShell({ data }: { data: PolicyData }) {
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const HeroIcon = data.icon;

  // Scroll to anchor on initial mount if URL has a hash.
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) {
        requestAnimationFrame(() =>
          el.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      }
    }
  }, []);

  const related = RELATED[data.slug];
  const sectionCount = data.sections.filter((s) => s.level !== 2).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] text-white print:bg-white print:text-black">
      {/* Top bar */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0F1419]/80 border-b border-gray-800/60 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to CashTap
          </Link>
          <img src="/logo.png" alt="CashTap" className="h-7 w-auto" />
          <button
            type="button"
            onClick={() => setMobileTocOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1 rounded-lg border border-gray-700/60"
            aria-expanded={mobileTocOpen}
          >
            {mobileTocOpen ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
            Contents
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero block — large icon tile, badges, title, intro */}
        <div className="relative">
          {/* Soft green halo behind the hero */}
          <div
            aria-hidden="true"
            className="absolute -inset-x-4 -top-8 -bottom-4 -z-10 bg-gradient-to-br from-[#61CE70]/[0.07] via-transparent to-transparent rounded-3xl blur-2xl"
          />
          <div className="max-w-3xl">
            <div className="flex flex-col sm:flex-row sm:items-start gap-5">
              {/* Hero icon tile */}
              <div className="relative shrink-0">
                <div
                  aria-hidden="true"
                  className="absolute -inset-2 rounded-[22px] bg-gradient-to-br from-[#61CE70]/30 via-[#61CE70]/10 to-transparent blur-lg"
                />
                <div className="relative w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl bg-gradient-to-br from-[#61CE70]/20 via-[#50B060]/10 to-transparent border border-[#61CE70]/30 flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(97,206,112,0.35),inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                  <HeroIcon className="w-8 h-8 sm:w-9 sm:h-9 text-[#61CE70]" strokeWidth={1.8} />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#61CE70]/10 border border-[#61CE70]/20 text-[#61CE70] text-[10px] font-semibold tracking-[0.18em] uppercase">
                  <FileText className="w-3 h-3" />
                  {data.documentType}
                </div>
                <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
                  {data.title}
                </h1>
                {data.subtitle && (
                  <p className="mt-2 text-gray-400 text-sm sm:text-[15px]">
                    {data.subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Meta pills */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-300 px-3 py-1.5 rounded-full border border-gray-800 bg-gray-900/50">
                <Calendar className="w-3.5 h-3.5 text-[#61CE70]" />
                <span className="text-gray-400">Effective</span>
                <span className="font-medium text-white">{data.effectiveDate}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-300 px-3 py-1.5 rounded-full border border-gray-800 bg-gray-900/50">
                <List className="w-3.5 h-3.5 text-[#61CE70]" />
                <span className="font-medium text-white">{sectionCount}</span>
                <span className="text-gray-400">sections</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-300 px-3 py-1.5 rounded-full border border-gray-800 bg-gray-900/50">
                <span className="text-gray-400">Version</span>
                <span className="font-medium text-white tabular-nums">{data.version}</span>
              </span>
            </div>

            {data.intro && (
              <p className="mt-7 text-gray-300 leading-relaxed text-[15px] border-l-2 border-[#61CE70]/30 pl-4">
                {data.intro}
              </p>
            )}
          </div>
        </div>

        {/* Mobile TOC drawer */}
        {mobileTocOpen && (
          <div className="lg:hidden mt-6 p-4 rounded-2xl bg-[#0F1419]/80 border border-gray-800 print:hidden">
            <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-gray-500 mb-3">
              <List className="w-3.5 h-3.5 text-[#61CE70]" />
              Contents
            </p>
            <div onClick={() => setMobileTocOpen(false)}>
              <TableOfContents sections={data.sections} />
            </div>
          </div>
        )}

        {/* Two-column layout: sticky TOC + content */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">
          <aside className="hidden lg:block print:hidden">
            <div className="sticky top-20">
              <div className="rounded-2xl bg-[#0F1419]/60 border border-gray-800/80 p-3">
                <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-gray-500 mb-3 px-2.5">
                  <List className="w-3.5 h-3.5 text-[#61CE70]" />
                  Contents
                </p>
                <div className="max-h-[calc(100vh-12rem)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700">
                  <TableOfContents sections={data.sections} />
                </div>
              </div>
            </div>
          </aside>

          <article className="min-w-0 max-w-3xl">
            {data.sections.map((s) => (
              <Section key={s.id} section={s} />
            ))}

            {data.footerNote && (
              <p className="mt-14 text-gray-500 text-xs italic border-t border-gray-800/60 pt-6 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[#61CE70]/50"
                />
                {data.footerNote}
              </p>
            )}
          </article>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 rounded-3xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/80 to-[#0B0F16]/80 p-6 sm:p-8 print:hidden">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-12 h-12 rounded-xl bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#61CE70]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold">
                Questions about this policy?
              </h3>
              <p className="text-gray-400 text-sm mt-0.5">
                Contact our Compliance team for clarifications or to exercise your rights.
              </p>
            </div>
            <Link
              to="/support"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#61CE70] hover:bg-[#50B060] text-black text-sm font-semibold transition-colors shrink-0"
            >
              Contact Support
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Cross-links */}
        <div className="mt-12 pt-8 border-t border-gray-800/60 print:hidden">
          <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-gray-500 mb-4">
            <FileText className="w-3.5 h-3.5 text-[#61CE70]" />
            Related Policies
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {related.map((r) => {
              const Icon = r.icon;
              return (
                <Link
                  key={r.slug}
                  to={r.href}
                  className="group relative overflow-hidden flex items-start gap-3 p-4 rounded-2xl bg-[#0F1419]/60 border border-gray-800 hover:border-[#61CE70]/40 hover:bg-[#0F1419] transition-all"
                >
                  <div
                    aria-hidden="true"
                    className="absolute -right-12 -top-12 w-32 h-32 rounded-full bg-[#61CE70]/[0.04] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <span className="relative w-10 h-10 rounded-xl bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0 group-hover:bg-[#61CE70]/15 transition-colors">
                    <Icon className="w-4 h-4 text-[#61CE70]" />
                  </span>
                  <div className="relative flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{r.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 truncate">{r.subtitle}</p>
                  </div>
                  <ChevronRight className="relative w-4 h-4 text-gray-500 group-hover:text-[#61CE70] group-hover:translate-x-0.5 transition-all shrink-0 mt-3" />
                </Link>
              );
            })}
          </div>
        </div>

        <footer className="mt-12 pt-6 border-t border-gray-800/40 text-center text-gray-600 text-xs">
          © {new Date().getFullYear()} CashTap.
        </footer>
      </main>
    </div>
  );
}
